import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import { sanitizeHtml } from '../../utils/sanitize';
import { PageHero } from '../../components/common/PageHero';

export const DynamicPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();

    const { data: page, isLoading, isError } = usePageMetadata(slug || '');

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-32 min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (isError || !page) {
        // Page not found -> Redirect to Home or 404
        return <Navigate to="/" replace />;
    }

    if (page.useCustomDesign === true) {
        // Template-based rendering: Allow the template to control full width (e.g. Hero sections, Nachhilfe, Uber Uns)
        return (
            <div className="flex flex-col bg-background min-h-screen overflow-x-hidden">
                <div
                    className="w-full prose-flex-safe"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.htmlContent) }}
                />
            </div>
        );
    }

    // Default Layout with PageHero (Cover Photo behind Title)
    return (
        <div className="flex flex-col bg-background min-h-screen pb-24">
            <PageHero
                title={page.title}
                breadcrumb={[
                    { label: 'Startseite', to: '/' },
                    { label: page.title }
                ]}
            />

            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 w-full -mt-12 relative z-10">
                <div className="bg-white rounded-3xl shadow-sm border border-border p-8 sm:p-16">
                    <div
                        className="prose prose-lg prose-primary max-w-none text-foreground/80 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.htmlContent) }}
                    />
                </div>
            </div>
        </div>
    );
};
